import { create } from "zustand"

// Types
export interface IncomingCall {
  type?: "user" | "contact"
  name: string
  number: string
  address?: string
  call_sid: string
  matter?: string
  contact?: string
}

export interface CurrentCall {
  name: string
  number: string
  address?: string
  call_sid: string
  matter?: string
  contact?: string
}

export interface TransferCall {
  _id?: string
  display_name?: string
  type?: string
  can_merge?: boolean
}

export interface QueueEntry {
  _id: string
  contact: {
    _id: string
    display_name?: string
    phone: string
    city?: string
    state?: string
  }
  division: {
    _id: string
    name: string
  }
  matter?: string
  type: "hold" | "callback"
  queue_entry_title?: string
  queue_entry_color?: string
  call?: string
}

export type BrowserDialingState = "not_setup" | "loading" | "ready" | "error"
export type AgentStatus = "available" | "away" | "dnd" | "on_call" | "ringing"

interface CallState {
  // Twilio state
  browserDialing: BrowserDialingState
  incomingCall: IncomingCall | null
  currentCall: CurrentCall | null
  transferCall: TransferCall | null

  // Call controls
  muted: boolean
  onHold: boolean

  // Queue
  queueEntry: QueueEntry | null

  // UI state
  showKeypad: boolean
  showTransferModal: boolean
  callStartTime: number | null

  // Agent status
  agentStatus: AgentStatus
  isInQueue: boolean

  // Actions
  setBrowserDialing: (state: BrowserDialingState) => void
  setIncomingCall: (call: IncomingCall | null) => void
  setCurrentCall: (call: CurrentCall | null) => void
  setTransferCall: (call: TransferCall | null) => void
  setMuted: (muted: boolean) => void
  setOnHold: (onHold: boolean) => void
  setQueueEntry: (entry: QueueEntry | null) => void
  setShowKeypad: (show: boolean) => void
  setShowTransferModal: (show: boolean) => void
  setCallStartTime: (time: number | null) => void
  setAgentStatus: (status: AgentStatus) => void
  setIsInQueue: (inQueue: boolean) => void

  // Computed
  getAgentStatus: () => AgentStatus

  // Reset
  resetCallState: () => void
}

export const useCallStore = create<CallState>((set, get) => ({
  // Initial state
  browserDialing: "not_setup",
  incomingCall: null,
  currentCall: null,
  transferCall: null,
  muted: false,
  onHold: false,
  queueEntry: null,
  showKeypad: false,
  showTransferModal: false,
  callStartTime: null,
  agentStatus: "available",
  isInQueue: false,

  // Actions
  setBrowserDialing: (state) => set({ browserDialing: state }),

  setIncomingCall: (call) =>
    set({
      incomingCall: call,
      agentStatus: call ? "ringing" : get().currentCall ? "on_call" : "available",
    }),

  setCurrentCall: (call) =>
    set({
      currentCall: call,
      callStartTime: call ? Date.now() : null,
      agentStatus: call ? "on_call" : "available",
      muted: false,
      onHold: false,
    }),

  setTransferCall: (call) => set({ transferCall: call }),

  setMuted: (muted) => set({ muted }),

  setOnHold: (onHold) => set({ onHold }),

  setQueueEntry: (entry) => set({ queueEntry: entry }),

  setShowKeypad: (show) => set({ showKeypad: show }),

  setShowTransferModal: (show) => set({ showTransferModal: show }),

  setCallStartTime: (time) => set({ callStartTime: time }),

  setAgentStatus: (status) => set({ agentStatus: status }),

  setIsInQueue: (inQueue) => set({ isInQueue: inQueue }),

  // Computed status based on call state
  getAgentStatus: () => {
    const state = get()
    if (state.currentCall) return "on_call"
    if (state.incomingCall) return "ringing"
    if (state.browserDialing !== "ready") return "available"
    return state.agentStatus
  },

  // Reset all call-related state
  resetCallState: () =>
    set({
      incomingCall: null,
      currentCall: null,
      transferCall: null,
      muted: false,
      onHold: false,
      showKeypad: false,
      showTransferModal: false,
      callStartTime: null,
      agentStatus: "available",
    }),
}))
